import classnames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../../components/basics/Button/Button'
import GroupAvatar from '../../components/GroupAvatar/GroupAvatar'
import GroupUserOperations from '../../components/GroupUserOperations/GroupUserOperations'
import { Group } from '../../models/Group'
import {
  userCanAddUserToSomeGroupAsMember,
  userCanViewPrivateUserInformation,
} from '../../state/calculations/permissions/userCan'
import { useUser } from '../../state/entities'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'

import styles from './ManageUserPage.module.scss'

/**********************************************************************
 * ManageUserPageGroupMemberships
 **********************************************************************/
export default React.memo(function ManageUserPageGroupMemberships({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const { user } = useUser(uuid)

  const groupMemberships = useMemo(() => {
    const g: { [uuid: string]: Group & { isMember?: boolean; isGroupManager?: boolean } } = {}
    for (const group of user.groups || []) {
      g[group.uuid] = g[group.uuid] || { ...group }
      g[group.uuid].isMember = true
    }
    for (const group of user.managedGroups || []) {
      g[group.uuid] = g[group.uuid] || { ...group }
      g[group.uuid].isGroupManager = true
    }
    return Array.from(Object.values(g))
  }, [user])

  return (
    <section className={styles.ManageUserPageGroupMemberships}>
      <header>
        <h1>
          {userCanViewPrivateUserInformation(currentUser, user)
            ? t('user.group memberships')
            : t('user.managed groups')}
        </h1>
        {userCanAddUserToSomeGroupAsMember(currentUser, user) && (
          <Button small component={Link} to={routes.AddGroupMembershipsToUser.url({ uuid })} preventScrollReset>
            {t('manage group.add to groups')}
          </Button>
        )}
      </header>

      <main className={classnames({ skeleton: user.isDummy })}>
        <table>
          <tbody>
            {(groupMemberships || []).map((group) => (
              <tr key={group.uuid}>
                <td>
                  <GroupUserOperations userUuid={uuid} groupUuid={group.uuid} />
                </td>
                <td>
                  <GroupAvatar group={group as any} />
                </td>
                <td className={styles.nameColumn}>
                  <Link to={routes.manageGroup.url({ uuid: group.uuid })}>{group.name}</Link>
                </td>
                <td className={styles.infoColumn}>
                  <span>{group.isMember && t(`user is group member`)}</span>
                  <span>{group.isGroupManager && t(`user is group manager`)}</span>
                </td>
              </tr>
            ))}
            {!(groupMemberships || []).length && (
              <tr>
                <td colSpan={4} className={styles.empty}>
                  {t('no group memberships found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </main>
    </section>
  )
})
